/**
 * Settings → Connected apps — the apps (Claude, Claude Code…) a person connected to Papan over MCP, each acting as
 * them in the organizations they chose. `/settings/connected-apps` lists them; `/settings/connected-apps/:id` is one
 * connection, inside Settings: which orgs it acts in (saved in place — the next call uses them) and Disconnect
 * (docs/plans/2026-10-12_REMOTE_MCP_REVIEW_FIXES.md §3.6; option B Phase 4).
 *
 * It replaced Developer tokens (owner, 2026-10-16, before either shipped): most people connect an app and never need
 * a token, and "developer tokens" means nothing to them. Connecting an app goes through Logto's sign-in instead.
 *
 * One connection used to be its own page outside the app (`/oauth/connections/:id`, still redirected here for links
 * Claude handed out); it looked disconnected from Papan and had no way back (owner, 2026-10-16).
 *
 * The save sends the org set the page loaded with; the server refuses it when that changed meanwhile (another tab), so
 * a stale page never puts back an org removed elsewhere. Turning an org on widens the connection — the server judges
 * it exactly as the consent page's decision.
 *
 * Per PERSON, not per org: a connection may span several orgs, so the page shows the same list in every org.
 *
 * Layout (owner, 2026-10-17): System Settings style — grouped inset cards with hairline rows (one card per group, never
 * a box per row), label-left / value-right info rows, a switch per org, and a save bar that appears only while there
 * is something to save.
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from '@papan/i18n'
import { request } from '@papan/api-client'
import { useBrand } from '@papan/brand/react'
import { AlertCircle, BadgeCheck, Check, CheckCircle2, ChevronLeft, ChevronRight, Copy, FilePlus2, FolderInput, Pencil, Plug, RotateCcw, Trash2 } from 'lucide-react'
import { AppTile, Group, GROUP_CARD, InfoRow, Notice, OrgSwitchRow, SecretsNote, SkeletonRows } from './GroupedList'
import { GlassToggle } from './GlassToggle'

interface ClientView { name: string; host: string; verified: boolean; publisher?: string | null; logoUrl?: string | null }
interface ConnectionRow {
  id: string
  client: ClientView
  orgs: Array<{ tenantId: string; name: string | null }>
  lastUsedAt: string | null
}
interface ConnectionOrg { tenantId: string; orgEmail: string; name: string | null; secrets?: boolean }
interface Connection {
  id: string
  client: ClientView
  orgs: ConnectionOrg[]
  createdAt: string
  lastUsedAt: string | null
  expiresAt: string
}
interface Identity { tenantId: string; name: string; type: string; mode: string; emails: string[]; mcpOff?: boolean; mcpSecretsOff?: boolean }

/** The orgs where a connection may use keybook secrets (#1297). */
const secretOrgs = (c: Connection): Set<string> => new Set(c.orgs.filter(o => o.secrets === true).map(o => o.tenantId))

const formatDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : null)

/** "2 days ago" in the reader's language; the date once it is more than a month old. */
function relativeTime(iso: string, lang?: string): string {
  const diffSec = (new Date(iso).getTime() - Date.now()) / 1000
  const abs = Math.abs(diffSec)
  if (abs > 30 * 86400) return formatDate(iso) ?? ''
  const rtf = new Intl.RelativeTimeFormat(lang || undefined, { numeric: 'auto' })
  if (abs < 60) return rtf.format(0, 'second')
  if (abs < 3600) return rtf.format(Math.round(diffSec / 60), 'minute')
  if (abs < 86400) return rtf.format(Math.round(diffSec / 3600), 'hour')
  return rtf.format(Math.round(diffSec / 86400), 'day')
}

/**
 * Room at the bottom of the page for the floating account bar (settings · avatar · sign-out, `absolute bottom-8`,
 * drawn over every page in the clear workspace): without it the last card sits under the bar (owner, 2026-10-17).
 */
const PAGE = 'max-w-2xl pb-28'
export interface ConnectedAppsPageProps {
  /** The connection open in the page (the URL's param); none = the list. */
  connectionId?: string
  /** Open one connection, or back to the list with `undefined`. */
  onNavigate?: (connectionId: string | undefined) => void
}

export function ConnectedAppsPage({ connectionId, onNavigate }: ConnectedAppsPageProps) {
  if (connectionId) return <ConnectionDetail key={connectionId} id={connectionId} onBack={() => onNavigate?.(undefined)} />
  return <ConnectionList onOpen={(id) => onNavigate?.(id)} />
}

function ConnectionList({ onOpen }: { onOpen: (id: string) => void }) {
  const { t, i18n } = useTranslation('core-ui')
  const { productName } = useBrand()
  const [rows, setRows] = useState<ConnectionRow[] | null>(null)
  const [serverUrl, setServerUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    request<{ connections: ConnectionRow[]; serverUrl?: string | null }>('/api/mcp-oauth/connections')
      .then(r => { if (!cancelled) { setRows(r.connections); setServerUrl(r.serverUrl ?? null) } })
      .catch(() => { if (!cancelled) setFailed(true) })
    return () => { cancelled = true }
  }, [])

  return (
    <div className={`${PAGE} space-y-6`} data-testid="connected-apps">
      <header className="space-y-1">
        <h2 className="text-xl font-semibold tracking-tight themed-text">{t('settings.connectedApps.title')}</h2>
        <p className="text-sm leading-relaxed themed-text-muted">{t('settings.connectedApps.description', { product: productName })}</p>
      </header>

      {failed
        ? (
          <p className="flex items-center gap-2 text-sm text-red-400" role="alert">
            <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />{t('settings.connectedApps.failed')}
          </p>
        )
        : rows === null
          ? <SkeletonRows />
          : rows.length === 0
            ? (
              <Notice
                testId="connected-apps-empty"
                icon={<Plug className="h-5 w-5 themed-text-muted" aria-hidden />}
                title={t('settings.connectedApps.emptyTitle')}
                body={t('settings.connectedApps.empty', { product: productName })}
              />
            )
            : (
              <Group footer={t('settings.connectedApps.footer')}>
                {rows.map(c => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => onOpen(c.id)}
                    className="group w-full text-left flex items-center gap-3 px-4 py-3 themed-bg-hover transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500"
                    data-testid={`connected-app-${c.id}`}
                  >
                    <AppTile name={c.client.name} host={c.client.host} logoUrl={c.client.logoUrl} />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5">
                        <span className="themed-text text-sm font-medium truncate">{c.client.name}</span>
                        {c.client.verified && <BadgeCheck className="h-3.5 w-3.5 shrink-0 text-blue-400" aria-label={t('settings.connectedApps.verified')} />}
                      </span>
                      <span className="themed-text-muted text-xs block truncate">{c.orgs.map(o => o.name ?? o.tenantId).join(' · ')}</span>
                    </span>
                    <span className="themed-text-muted text-xs shrink-0 hidden sm:block">
                      {c.lastUsedAt ? t('settings.connectedApps.lastUsed', { date: relativeTime(c.lastUsedAt, i18n?.language) }) : t('settings.connectedApps.neverUsed')}
                    </span>
                    <ChevronRight className="h-4 w-4 shrink-0 themed-text-muted opacity-60 group-hover:opacity-100 transition-opacity" aria-hidden />
                  </button>
                ))}
              </Group>
            )}

      {serverUrl && <ConnectGuide serverUrl={serverUrl} productName={productName} />}
    </div>
  )
}

type GuideApp = 'claude' | 'claudeCode' | 'chatgpt' | 'other'

/** A value with a Copy button — the server address, the Claude Code command. */
function CopyField({ value, mono = true, testId }: { value: string; mono?: boolean; testId?: string }) {
  const { t } = useTranslation('core-ui')
  const [copied, setCopied] = useState(false)
  const copy = () => {
    navigator.clipboard?.writeText(value).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }).catch(() => {})
  }
  return (
    <div className="flex items-center gap-2 rounded-lg themed-bg-tertiary px-3 py-2">
      <code className={`min-w-0 flex-1 truncate text-xs themed-text ${mono ? 'font-mono' : ''}`} data-testid={testId} title={value}>{value}</code>
      <button
        type="button"
        onClick={copy}
        className="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-xs text-blue-400 hover:bg-blue-500/10"
        aria-label={t('settings.connectedApps.guide.copy')}
      >
        {copied ? <Check className="h-3.5 w-3.5" aria-hidden /> : <Copy className="h-3.5 w-3.5" aria-hidden />}
        {copied ? t('settings.connectedApps.guide.copied') : t('settings.connectedApps.guide.copy')}
      </button>
    </div>
  )
}

/**
 * How to connect an AI app: the server address (exactly the resource tokens are issued for — from the server, never
 * built from the browser's host) and, per app, the few steps to add it. Every app ends the same way: sign in here and
 * choose the organizations it may use.
 */
function ConnectGuide({ serverUrl, productName }: { serverUrl: string; productName: string }) {
  const { t } = useTranslation('core-ui')
  const [app, setApp] = useState<GuideApp>('claude')
  // A name for `claude mcp add`: the product, lower-case, letters and digits only.
  const serverName = productName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'mcp'
  const g = (k: string, o?: Record<string, unknown>) => t(`settings.connectedApps.guide.${k}`, { product: productName, ...o })
  const steps: Record<GuideApp, ReactNode[]> = {
    claude: [g('claude1'), g('claude2'), g('finish')],
    claudeCode: [<>{g('code1')}<div className="mt-2"><CopyField value={`claude mcp add --transport http ${serverName} ${serverUrl}`} testId="connect-guide-command" /></div></>, g('code2', { name: serverName }), g('finish')],
    // ChatGPT: OpenAI's "Add a custom MCP server" flow (Plugins → + → Create as a plugin; used with @ in a chat).
    chatgpt: [g('chatgpt1'), g('chatgpt2'), g('chatgpt3'), g('finish'), g('chatgpt4')],
    other: [g('other1'), g('finish')],
  }
  const notes: Partial<Record<GuideApp, string>> = { claude: g('claudeNote'), chatgpt: g('chatgptNote') }
  return (
    <section className="space-y-1.5" data-testid="connect-guide">
      <h3 className="px-4 text-[11px] font-medium uppercase tracking-wider themed-text-muted">{g('title')}</h3>
      <div className={`${GROUP_CARD} p-4 space-y-4`}>
        <div className="space-y-1.5">
          <p className="text-sm font-medium themed-text">{g('serverLabel')}</p>
          <CopyField value={serverUrl} testId="connect-guide-url" />
        </div>
        <GlassToggle<GuideApp>
          value={app}
          onChange={setApp}
          tablist
          options={[
            { value: 'claude', label: g('appClaude'), testId: 'connect-guide-claude' },
            { value: 'claudeCode', label: g('appClaudeCode'), testId: 'connect-guide-claude-code' },
            { value: 'chatgpt', label: g('appChatgpt'), testId: 'connect-guide-chatgpt' },
            { value: 'other', label: g('appOther'), testId: 'connect-guide-other' },
          ]}
        />
        <ol className="space-y-3" data-testid={`connect-guide-steps-${app}`}>
          {steps[app].map((step, i) => (
            <li key={i} className="flex gap-3 text-sm">
              <span aria-hidden className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-500/15 text-xs font-semibold text-blue-400">{i + 1}</span>
              <div className="min-w-0 flex-1 pt-0.5 themed-text leading-relaxed">{step}</div>
            </li>
          ))}
        </ol>
        {notes[app] && <p className="text-xs leading-relaxed themed-text-muted">{notes[app]}</p>}
      </div>
    </section>
  )
}

interface ActivityRow { eventType: 'created' | 'updated' | 'moved' | 'deleted' | 'restored'; name: string | null; oldName: string | null; orgName: string | null; at: string }

const ACTIVITY_ICON = {
  created: FilePlus2, updated: Pencil, moved: FolderInput, deleted: Trash2, restored: RotateCcw,
} as const

/**
 * What this connection changed (#1230), newest first: so a person can notice a connection doing what they didn't ask
 * for. Only its own writes, as them, in orgs they still belong to (the server decides).
 */
function RecentActivity({ id }: { id: string }) {
  const { t, i18n } = useTranslation('auth')
  const [rows, setRows] = useState<ActivityRow[] | null>(null)
  useEffect(() => {
    let cancelled = false
    request<{ activity: ActivityRow[] }>(`/api/mcp-oauth/connections/${encodeURIComponent(id)}/activity`)
      .then(r => { if (!cancelled) setRows(r.activity) })
      .catch(() => { if (!cancelled) setRows([]) })
    return () => { cancelled = true }
  }, [id])
  if (rows === null) return null
  return (
    <Group header={t('mcpConnections.activity.title')} testId="connection-activity">
      {rows.length === 0
        ? <p className="px-4 py-3 text-sm themed-text-muted">{t('mcpConnections.activity.none')}</p>
        : rows.map((r, i) => {
          const Icon = ACTIVITY_ICON[r.eventType] ?? Pencil
          return (
            <div key={i} className="flex items-center gap-3 px-4 py-2.5 text-sm" data-testid="connection-activity-row">
              <Icon className="h-4 w-4 shrink-0 themed-text-muted" aria-hidden />
              <span className="min-w-0 flex-1">
                <span className="block truncate themed-text">
                  {t(`mcpConnections.activity.${r.eventType}`, { name: r.name ?? t('mcpConnections.activity.anItem'), oldName: r.oldName ?? '' })}
                </span>
                {r.orgName && <span className="block truncate text-xs themed-text-muted">{r.orgName}</span>}
              </span>
              <span className="shrink-0 text-xs themed-text-muted">{relativeTime(r.at, i18n?.language)}</span>
            </div>
          )
        })}
    </Group>
  )
}

type View =
  | { kind: 'loading' }
  | { kind: 'gone'; message: string }
  | { kind: 'ready'; connection: Connection; identities: Identity[] }
  | { kind: 'disconnected'; client: string }

function ConnectionDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const { t, i18n } = useTranslation('auth')
  const { productName } = useBrand()
  const [view, setView] = useState<View>({ kind: 'loading' })
  /** tenantId → the email it acts as there; present = on. */
  const [picked, setPicked] = useState<Map<string, string>>(new Map())
  /** #1297: the orgs where it may use keybook secrets in app runs. */
  const [secrets, setSecrets] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [confirmDisconnect, setConfirmDisconnect] = useState(false)

  const load = useCallback(() => {
    setView({ kind: 'loading' })
    setError(null)
    request<{ connection: Connection; identities: Identity[] }>(`/api/mcp-oauth/connections/${encodeURIComponent(id)}`)
      .then(({ connection, identities }) => {
        setPicked(new Map(connection.orgs.map(o => [o.tenantId, o.orgEmail])))
        setSecrets(secretOrgs(connection))
        setView({ kind: 'ready', connection, identities })
      })
      .catch((e: Error & { code?: string }) => {
        setView({ kind: 'gone', message: e.code === 'mcp_connection_gone' ? t('mcpConnections.gone') : (e.message || t('mcpConnections.failed')) })
      })
  }, [id, t])

  useEffect(() => { load() }, [load])

  const toggleOrg = useCallback((identity: Identity, on: boolean) => {
    setSaved(false)
    setError(null)
    setPicked((prev) => {
      const next = new Map(prev)
      if (on) next.set(identity.tenantId, prev.get(identity.tenantId) ?? identity.emails[0])
      else next.delete(identity.tenantId)
      return next
    })
    // An org turned off loses its secrets choice with it; turned back on, it starts off.
    if (!on) setSecrets(prev => { const next = new Set(prev); next.delete(identity.tenantId); return next })
  }, [])

  const back = (
    <button
      type="button"
      onClick={onBack}
      className="-ml-1 inline-flex items-center gap-0.5 rounded px-1 text-sm text-blue-400 hover:text-blue-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      data-testid="connected-app-back"
    >
      <ChevronLeft className="h-4 w-4" aria-hidden />{t('mcpConnections.all')}
    </button>
  )

  if (view.kind === 'loading') return <div className={`${PAGE} space-y-6`}>{back}<SkeletonRows rows={3} /></div>
  if (view.kind === 'gone') {
    return (
      <div className={`${PAGE} space-y-6`}>
        {back}
        <Notice role="alert" icon={<AlertCircle className="h-6 w-6 themed-text-muted" aria-hidden />} body={view.message} />
      </div>
    )
  }
  if (view.kind === 'disconnected') {
    return (
      <div className={`${PAGE} space-y-6`}>
        {back}
        <Notice role="status" tone="success" icon={<CheckCircle2 className="h-6 w-6 text-emerald-400" aria-hidden />}
          body={t('mcpConnections.disconnected', { client: view.client })} />
      </div>
    )
  }

  const { connection, identities } = view
  const stored = new Map(connection.orgs.map(o => [o.tenantId, o.orgEmail]))
  const storedSecrets = secretOrgs(connection)
  const changed = picked.size !== stored.size || [...picked].some(([k, v]) => stored.get(k) !== v)
    || secrets.size !== storedSecrets.size || [...secrets].some(k => !storedSecrets.has(k))

  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      const { connection: fresh } = await request<{ connection: Connection | null }>(`/api/mcp-oauth/connections/${encodeURIComponent(id)}/orgs`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orgs: [...picked].map(([tenantId, orgEmail]) => ({ tenantId, orgEmail, secrets: secrets.has(tenantId) })),
          expected: connection.orgs.map(({ tenantId, orgEmail, secrets: s }) => ({ tenantId, orgEmail, secrets: s === true })),
        }),
      })
      if (fresh) {
        setPicked(new Map(fresh.orgs.map(o => [o.tenantId, o.orgEmail])))
        setSecrets(secretOrgs(fresh))
        setView({ kind: 'ready', connection: fresh, identities })
      }
      setSaved(true)
    } catch (e) {
      const err = e as Error & { code?: string }
      if (err.code === 'mcp_connection_stale') setError(t('mcpConnections.stale'))
      else if (err.code === 'mcp_connection_gone') setView({ kind: 'gone', message: t('mcpConnections.gone') })
      else setError(err.message || t('mcpConnections.failed'))
    } finally {
      setBusy(false)
    }
  }

  const revert = () => { setPicked(new Map(stored)); setSecrets(storedSecrets); setError(null) }

  const disconnect = async () => {
    setBusy(true)
    setError(null)
    try {
      await request(`/api/mcp-oauth/connections/${encodeURIComponent(id)}`, { method: 'DELETE' })
      setView({ kind: 'disconnected', client: connection.client.name })
    } catch (e) {
      const err = e as Error & { code?: string }
      if (err.code === 'mcp_connection_gone') setView({ kind: 'gone', message: t('mcpConnections.gone') })
      else setError(err.message || t('mcpConnections.failed'))
      setBusy(false)
    }
  }

  return (
    <div className={`${PAGE} space-y-7`} data-testid="mcp-connection">
      {back}

      <header className="flex items-center gap-4">
        <AppTile name={connection.client.name} host={connection.client.host} size="lg" logoUrl={connection.client.logoUrl} />
        <div className="min-w-0">
          <h2 className="text-xl font-semibold tracking-tight themed-text truncate">{connection.client.name}</h2>
          {connection.client.verified
            ? (
              <p className="mt-0.5 inline-flex items-center gap-1 text-xs text-blue-400">
                <BadgeCheck className="h-3.5 w-3.5" aria-hidden />{t('mcpConnections.verified', { host: connection.client.publisher ?? connection.client.host })}
              </p>
            )
            : connection.client.host && (
              <p className="mt-0.5 text-xs text-amber-400">{t('mcpConnections.unverifiedHost', { host: connection.client.host })}</p>
            )}
        </div>
      </header>

      <Group header={t('mcpConnections.details')}>
        <InfoRow label={t('mcpConnections.connectedLabel')} value={formatDate(connection.createdAt)} />
        <InfoRow
          label={t('mcpConnections.lastUsedLabel')}
          value={connection.lastUsedAt ? relativeTime(connection.lastUsedAt, i18n?.language) : t('mcpConnections.notYet')}
        />
        <InfoRow label={t('mcpConnections.endsLabel')} value={formatDate(connection.expiresAt)} />
      </Group>

      <Group
        header={t('mcpConnections.orgsLabel')}
        footer={<>
          {t('mcpConnections.orgsHint')} {t('mcpConnect.actsAsYou')}
          {/* What the keybook switches mean, said once — not under every org (#1297). */}
          {picked.size > 0 && <SecretsNote>{t('mcpConnect.secretsBody', { client: connection.client.name, product: productName })}</SecretsNote>}
        </>}
      >
        {identities.map((identity) => {
          const email = picked.get(identity.tenantId)
          // An org that turned MCP off can be removed from the connection, never added.
          const unavailable = identity.mode !== 'full' || (!!identity.mcpOff && email === undefined)
          return (
            <OrgSwitchRow
              key={identity.tenantId}
              identity={identity}
              email={email}
              disabled={unavailable || busy}
              dimmed={unavailable}
              onToggle={(on) => toggleOrg(identity, on)}
              onEmail={(e) => { setSaved(false); setPicked(prev => new Map(prev).set(identity.tenantId, e)) }}
              labels={{ noDrive: t('mcpConnect.noDrive'), orgOff: t('mcpConnect.orgOff'), asEmail: t('mcpConnect.asEmail') }}
              testId={`mcp-connection-org-${identity.tenantId}`}
              offTestId={`mcp-connection-org-off-${identity.tenantId}`}
              secrets={{
                on: secrets.has(identity.tenantId),
                // Turning it on goes through the same human-gated save; an org whose policy forbids it can't be turned on.
                disabled: busy || (!!identity.mcpSecretsOff && !storedSecrets.has(identity.tenantId) && !secrets.has(identity.tenantId)),
                onToggle: (on) => { setSaved(false); setError(null); setSecrets(prev => { const next = new Set(prev); if (on) next.add(identity.tenantId); else next.delete(identity.tenantId); return next }) },
                labels: { title: t('mcpConnect.secretsTitle'), policyOff: t('mcpConnect.secretsPolicyOff') },
                testId: `mcp-connection-secrets-${identity.tenantId}`,
              }}
            />
          )
        })}
      </Group>

      <RecentActivity id={id} />

      {(changed || error || saved) && (
        <div className={`${GROUP_CARD} sticky bottom-24 z-10 flex items-center gap-3 px-4 py-3 shadow-lg`} role={error ? 'alert' : 'status'}>
          <span className={`min-w-0 flex-1 text-sm ${error ? 'text-red-400' : 'themed-text'}`}>
            {error
              ?? (changed
                ? (picked.size === 0 ? t('mcpConnections.keepOne') : t('mcpConnections.unsaved'))
                : <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-400" aria-hidden />{t('mcpConnections.saved')}</span>)}
          </span>
          {changed && (
            <>
              <button type="button" onClick={revert} disabled={busy} className="rounded-lg px-3 py-1.5 text-sm themed-text themed-bg-hover disabled:opacity-50">
                {t('mcpConnections.revert')}
              </button>
              <button
                type="button"
                onClick={save}
                disabled={busy || picked.size === 0}
                className="rounded-lg px-4 py-1.5 text-sm font-medium themed-btn-primary disabled:opacity-50"
                data-testid="mcp-connection-save"
              >
                {t('mcpConnections.save')}
              </button>
            </>
          )}
        </div>
      )}

      <Group>
        {!confirmDisconnect
          ? (
            <button
              type="button"
              onClick={() => setConfirmDisconnect(true)}
              disabled={busy}
              className="w-full px-4 py-3 text-sm text-red-400 text-left themed-bg-hover transition-colors disabled:opacity-50"
              data-testid="mcp-connection-disconnect"
            >
              {t('mcpConnections.disconnectApp', { client: connection.client.name })}
            </button>
          )
          : (
            <div className="px-4 py-4 space-y-3">
              <p className="text-sm themed-text">{t('mcpConnections.disconnectConfirm', { client: connection.client.name })}</p>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setConfirmDisconnect(false)} disabled={busy}
                  className="rounded-lg px-3 py-1.5 text-sm themed-text themed-bg-hover disabled:opacity-50">{t('mcpConnect.cancel')}</button>
                <button type="button" onClick={disconnect} disabled={busy}
                  className="rounded-lg px-4 py-1.5 text-sm font-medium bg-red-600 text-white hover:bg-red-500 disabled:opacity-50" data-testid="mcp-connection-disconnect-confirm">
                  {t('mcpConnections.disconnect')}
                </button>
              </div>
            </div>
          )}
      </Group>
    </div>
  )
}
